import { ProductCommissionTypeEnum, ProductPremiumCalculationEnum, ProductRelationToPrincipalEnum } from './../../../../../../service/dto-insurance';
import { InsurancePremiumRecipientDto } from './../../../../../../service/dto-insurance';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { HttpInsuranceProductService } from '../../../../../../service/http-insurance-product.service';

@Component({
	selector: 'app-product-edit-extended-modal',
	standalone: false,
	templateUrl: './product-edit-extended-modal.component.html',
	styleUrl: './product-edit-extended-modal.component.scss'
})
export class ProductEditExtendedModalComponent implements OnInit, OnDestroy {
	@Input() formArray!: FormArray;
	@Input() formControl?: FormGroup;

	form!: FormGroup;

	/** Tracks expand state per detail row */
	detailExpanded: boolean[] = [];

	commissionRecipients: InsurancePremiumRecipientDto[] = [];
	recipientsLoading = true;

	availableRelations: ProductRelationToPrincipalEnum[] = [
		'Spouse', 'Child', 'ChildStillBorn', 'ChildStudent', 'ChildDisabled', 'Parent', 'ParentInLaw', 'Extended', 'All'
	];
	availablePremiumTypes: ProductPremiumCalculationEnum[] = ['Fixed', 'Percentage'];
	availableCommissionTypes: ProductCommissionTypeEnum[] = ['Fixed', 'Percentage'];
	compareById = (o1: any, o2: any): boolean => o1?.id === o2?.id;

	constructor(
		private fb: FormBuilder,
		private modalRef: NzModalRef,
		private httpService: HttpInsuranceProductService
	) { }

	ngOnInit(): void {
		const existing = this.formControl?.getRawValue();

		this.form = this.fb.group({
			id: [existing?.id ?? ''],
			name: [existing?.name ?? '', Validators.required],
			description: [existing?.description ?? ''],
			details: this.fb.array(
				(existing?.details ?? []).map((d: any) => this.buildDetailGroup(d))
			)
		});

		this.detailExpanded = (existing?.details ?? []).map(() => false);

		this.httpService.getPremiumRecipients().subscribe(recipients => {
			this.commissionRecipients = recipients;
			this.recipientsLoading = false;
		});
	}

	ngOnDestroy(): void { }

	// ─── Cast helper ─────────────────────────────────────────────────────────

	asGroup(ctrl: AbstractControl): FormGroup {
		return ctrl as FormGroup;
	}

	// ─── FormArray accessors ──────────────────────────────────────────────────

	get detailsArray(): FormArray {
		return this.form.get('details') as FormArray;
	}

	getDetailCommissions(detailIndex: number): FormArray {
		return this.detailsArray.at(detailIndex).get('commissions') as FormArray;
	}

	// ─── Group builders ───────────────────────────────────────────────────────

	private buildDetailGroup(item?: any): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			fromDate: [item?.fromDate ? new Date(item.fromDate) : null, Validators.required],
			toDate: [item?.toDate ? new Date(item.toDate) : null, Validators.required],
			fromAge: [item?.fromAge ?? 0, Validators.required],
			toAge: [item?.toAge ?? 0, Validators.required],
			relationToMainMember: [item?.relationToMainMember ?? 'All', Validators.required],
			premiumType: [item?.premiumType ?? 'Fixed', Validators.required],
			premiumAmount: [item?.premiumAmount ?? 0, Validators.required],
			benefitAmount: [item?.benefitAmount ?? 0, Validators.required],
			commissions: this.fb.array(
				(item?.commissions ?? []).map((c: any) => this.buildCommissionGroup(c))
			),
		});
	}

	private buildCommissionGroup(item?: any): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			recipient: [item?.recipient ?? null, Validators.required],
			commissionType: [item?.commissionType ?? 'Fixed', Validators.required],
			commissionAmount: [item?.commissionAmount ?? 0, Validators.required],
		});
	}

	// ─── Details CRUD ─────────────────────────────────────────────────────────

	addDetail(): void {
		this.detailsArray.push(this.buildDetailGroup());
		this.detailExpanded.push(false);
	}

	removeDetail(index: number): void {
		this.detailsArray.removeAt(index);
		this.detailExpanded.splice(index, 1);
	}

	// ─── Commissions CRUD ─────────────────────────────────────────────────────

	addCommission(detailIndex: number): void {
		this.getDetailCommissions(detailIndex).push(this.buildCommissionGroup());
		this.detailExpanded[detailIndex] = true;
	}

	removeCommission(detailIndex: number, commissionIndex: number): void {
		this.getDetailCommissions(detailIndex).removeAt(commissionIndex);
	}

	recipientName(id: number): string {
		return this.commissionRecipients.find(r => r.id === id)?.name ?? `#${id}`;
	}

	// ─── Modal actions ────────────────────────────────────────────────────────

	onCancel(): void {
		this.modalRef.close();
	}

	onSave(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const extended = this.form.getRawValue();

		if (this.formControl) {
			this.formControl.patchValue({ id: extended.id, name: extended.name, description: extended.description });
			const targetDetails = this.formControl.get('details') as FormArray;
			targetDetails.clear();
			extended.details.forEach((d: any) => targetDetails.push(this.buildDetailGroup(d)));
		} else {
			this.formArray.push(this.fb.group({
				id: [extended.id],
				name: [extended.name, Validators.required],
				description: [extended.description],
				details: this.fb.array(
					extended.details.map((d: any) => this.buildDetailGroup(d))
				)
			}));
		}

		this.modalRef.close(extended);
	}
}
